import type { ProductListingResolution } from "@adpilot/shared";
import { useMutation } from "@tanstack/react-query";
import { type MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDelayedPending } from "@/hooks/use-delayed-pending";
import { resolveProductListings } from "@/lib/product-listing-api";

const MAX_RESOLVE_IDENTIFIERS = 500;
const RESOLVE_LOADING_DELAY_MS = 150;

interface UseProductResolutionOptions {
  shopId: string;
  enterInput: string;
  onEnterInputChange: (input: string) => void;
  listingAdProduct: "sp" | undefined;
  mounted: MutableRefObject<boolean>;
  actionVersion: MutableRefObject<number>;
}

/** Resolves pasted SKU/ASIN identifiers into listings the user can add one by one. */
export function useProductResolution({
  shopId,
  enterInput,
  onEnterInputChange,
  listingAdProduct,
  mounted,
  actionVersion,
}: UseProductResolutionOptions) {
  const { t } = useTranslation("campaignCreation");
  const [resolutions, setResolutions] = useState<ProductListingResolution[]>([]);
  const [resolveInputError, setResolveInputError] = useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: ({ identifiers }: { identifiers: string[]; version: number }) =>
      resolveProductListings(shopId, identifiers, listingAdProduct),
    onError: () => toast.error(t("products.loadError")),
    onSuccess: ({ results }, variables) => {
      if (mounted.current && variables.version === actionVersion.current) setResolutions(results);
    },
  });
  const resolveLoadingVisible = useDelayedPending(resolve.isPending, RESOLVE_LOADING_DELAY_MS);
  function resolveEnteredProducts() {
    const identifiers = enterInput
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (identifiers.length > MAX_RESOLVE_IDENTIFIERS) {
      setResolutions([]);
      setResolveInputError(
        t("products.enter.limit", {
          count: identifiers.length,
          max: MAX_RESOLVE_IDENTIFIERS,
        }),
      );
      return;
    }
    setResolveInputError(null);
    resolve.mutate({ identifiers, version: actionVersion.current });
  }
  function changeEnterInput(value: string) {
    setResolveInputError(null);
    setResolutions([]);
    onEnterInputChange(value);
  }

  const resolutionRows = useMemo(() => {
    const occurrenceByIdentity = new Map<string, number>();
    return resolutions.map((resolution) => {
      const identity = `${resolution.type}:${resolution.identifier}`;
      const occurrence = (occurrenceByIdentity.get(identity) ?? 0) + 1;
      occurrenceByIdentity.set(identity, occurrence);
      return { key: `${identity}:${occurrence}`, resolution };
    });
  }, [resolutions]);

  return {
    resolutions,
    resolutionRows,
    resolveInputError,
    resolvePending: resolve.isPending,
    resolveFailed: resolve.isError,
    resolveLoadingVisible,
    resolveEnteredProducts,
    changeEnterInput,
  };
}

export type ProductResolution = ReturnType<typeof useProductResolution>;
